import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Tag, Space, Select, Input, Typography, Button, Tooltip, message, Popconfirm,
  Tabs, Modal, Form, InputNumber, DatePicker, Switch, Alert, Row, Col, Descriptions, Segmented,
} from 'antd';
import {
  WifiOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, SendOutlined,
  DownloadOutlined, CopyOutlined, ThunderboltOutlined, ClockCircleOutlined, StopOutlined,
  AppstoreOutlined, EnvironmentOutlined, HddOutlined, LineChartOutlined, CloudUploadOutlined, ExclamationCircleOutlined, BookOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { thloggerAPI, scheduleAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { brand, gray, state } from '../theme/tokens';
import { font } from '../theme';
import { EmptyState } from '../components/common';
import ThLoggerSetup from '../components/ThLoggerSetup';
import { useFitTableHeight, useFillHeight } from '../hooks/useViewportFit';
import { useFlexColumn, FLEX_SPACER } from '../hooks/useFlexColumn';
import { fmtDate, fmtDateTime, DATE_FORMAT } from '../utils/date';
import ThLoggerGuide from '../components/ThLoggerGuide';
import ThLoggerOverview, { CardStylePicker } from '../components/ThLoggerOverview';
import ThLoggerPoints from '../components/ThLoggerPoints';
import UplinkDetail from '../components/ThLoggerUplinkDetail';
import { ReceptionStrip, ReceptionLegend, summarize, devIssues } from '../components/ThLoggerReception';
import { BatteryBadge, SignalBadge, OptimizationBadge, signalGrade, batteryGrade, evaluateOptimization } from '../components/ThLoggerBadges';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/* 수신 등급 — 색만으로 구분하지 않는다: 라벨·아이콘을 항상 함께 적는다 (알림센터와 같은 규칙) */
const LEVELS: Record<string, { label: string; color: string; bg: string; desc: string }> = {
  OK:   { label: '수신 정상', color: state.successText, bg: state.successSoft, desc: '마지막 업링크가 주기 안에 있음' },
  WARN: { label: '수신 지연', color: state.warningText, bg: state.warningSoft, desc: '업링크 주기 + 1시간 초과' },
  CRIT: { label: '미수신',   color: state.errorText,   bg: state.errorSoft,   desc: '업링크 주기 × 2 + 1시간 초과' },
  NEW:  { label: '수신 대기', color: gray[600],         bg: gray[100],         desc: '등록 후 첫 업링크 전' },
};
const LEVEL_ORDER = ['OK', 'WARN', 'CRIT', 'NEW'];
const LEVEL_BAR: Record<string, string> = { OK: '#1baf7a', WARN: '#eda100', CRIT: '#e5484d', NEW: '#94a3b8' };


const pagination = (page: number, pageSize: number, total: number, onChange: (p: number, s: number) => void) => ({
  current: page, pageSize, total, size: 'small' as const,
  showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
  showTotal: (t: number, r: [number, number]) => `${r[0]}–${r[1]} / 총 ${t}건`,
  onChange,
});

/* 주기 변경 프리셋 — 설정 가이드 '전송 간격 · 시각' 탭의 31일 실측과 같은 조합 */
const CFG_PRESETS = [
  { key: '24h', label: '1일 1회 · 08:00 · N32 (적용 기준)', tdc: 86400, n: 32 },
  { key: '8h', label: '8시간 · 1일 3회 (퇴근 전 확인이 필요할 때)', tdc: 28800, n: 16 },
  { key: '12h', label: '12시간 · 1일 2회', tdc: 43200, n: 24 },
  { key: 'test', label: '1시간 (설치 시험용 · 하루만)', tdc: 3600, n: 4 },
];
/* 월(31일) 데이터 추정 — 권장 형식 HEX(AT+PRO=2,0) 바이너리: 헤더 53B + 항목 8B×N, 세션당 IP/UDP 28B + ACK 39B
   (JSON 은 실측 회귀 246 + 39.4×N 바이트로 약 4배) */
const estMonthKB = (tdc: number, n: number) => Math.round(((53 + 8 * n + 28 + 39) * (31 * 86400 / tdc)) / 1024 * 10) / 10;
const CFG_STATUS: Record<string, { label: string; color: string; desc: string }> = {
  pending:   { label: '전송 대기', color: 'blue',    desc: '다음 업링크 때 기기로 보낸다' },
  sent:      { label: '확인 대기', color: 'gold',    desc: '보냈고 기기의 확인 응답 또는 업로드 간격 변화를 기다리는 중' },
  applied:   { label: '반영됨',   color: 'green',   desc: '기기에 반영된 것을 확인했다' },
  failed:    { label: '반영 실패', color: 'red',     desc: '여러 번 보냈지만 확인이 없다 — 단말 설정을 직접 확인' },
  cancelled: { label: '취소됨',   color: 'default', desc: '예약을 취소했다' },
};

const tdcLabel = (sec?: number) => (!sec ? '-' : 86400 % sec === 0 && sec >= 3600 ? `1일 ${86400 / sec}회` : `${Math.round((sec / 3600) * 10) / 10}h`);
const copy = (text: string) => navigator.clipboard?.writeText(text).then(() => message.success('복사되었습니다.')).catch(() => message.error('복사에 실패했습니다.'));

/* ────────────────────────── 상단 요약 ────────────────────────── */
const SummaryBar: React.FC<{ summary: any; devices: any[]; onPick: (lv: string | null) => void; picked: string | null }> = ({ summary, devices, onPick, picked }) => {
  const total = summary?.devices_active || 0;
  const active = devices.filter(d => d.is_active);
  const weakSignal = active.filter(d => signalGrade(d.last_signal_dbm)?.key === 'weak').length;
  const battWarn = active.filter(d => batteryGrade(d.last_battery_v)?.key === 'warn').length;
  const by = summary?.by_level || {};
  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: '8px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Space size={6}>
          <WifiOutlined style={{ color: brand.primary }} />
          <Text strong style={{ fontSize: 15 }}>활성 장치</Text>
          <Text strong style={{ fontSize: 20, fontVariantNumeric: 'tabular-nums' }}>{total}</Text>
          <Text type="secondary">대</Text>
          {summary?.devices_total > total && <Text type="secondary" style={{ fontSize: 12 }}>(비활성 {summary.devices_total - total})</Text>}
        </Space>
        <Space size={6} wrap style={{ marginLeft: 'auto' }}>
          <Tag icon={<ClockCircleOutlined />} style={{ margin: 0 }}>24h 업링크 {summary?.uplinks_24h ?? 0}</Tag>
          <Tag style={{ margin: 0 }}>24h 저장 {summary?.readings_24h ?? 0}건</Tag>
          {(summary?.rejects_24h ?? 0) > 0 && <Tag color="volcano" style={{ margin: 0 }}>24h 거절 {summary.rejects_24h}</Tag>}
          {(summary?.low_battery ?? 0) > 0 && <Tag icon={<ThunderboltOutlined />} color="red" style={{ margin: 0 }}>배터리 교체 {summary.low_battery}대</Tag>}
          {weakSignal > 0 && <Tag color="volcano" style={{ margin: 0 }}>신호 약함 {weakSignal}대</Tag>}
          {battWarn > 0 && <Tag color="orange" style={{ margin: 0 }}>배터리 주의 {battWarn}대</Tag>}
          <Tag style={{ margin: 0, color: gray[500] }}>누적 {summary?.readings_total ?? 0}건</Tag>
        </Space>
      </div>
      {total > 0 && (
        <>
          <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', gap: 2, marginTop: 8, background: gray[100] }}>
            {LEVEL_ORDER.filter(lv => by[lv] > 0).map(lv => (
              <Tooltip key={lv} title={`${LEVELS[lv].label} ${by[lv]}대`}>
                <div onClick={() => onPick(picked === lv ? null : lv)}
                  style={{ flex: by[lv], background: LEVEL_BAR[lv], cursor: 'pointer', opacity: picked && picked !== lv ? 0.3 : 1 }} />
              </Tooltip>
            ))}
          </div>
          <Space size={14} wrap style={{ marginTop: 6, fontSize: 12 }}>
            {LEVEL_ORDER.map(lv => (
              <span key={lv} onClick={() => onPick(picked === lv ? null : lv)}
                style={{ cursor: 'pointer', color: gray[700], fontWeight: picked === lv ? 700 : 500 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: LEVEL_BAR[lv], marginRight: 5 }} />
                {LEVELS[lv].label} {by[lv] || 0}대{total ? ` (${Math.round(((by[lv] || 0) / total) * 100)}%)` : ''}
              </span>
            ))}
            {picked && <Tag closable onClose={() => onPick(null)} style={{ margin: 0 }}>{LEVELS[picked].label}만 표시 중</Tag>}
          </Space>
        </>
      )}
    </Card>
  );
};

/* 최근 업로드 칸 — 경과 시간을 TDC 에 견줘 배경색으로 (2026-11-04 사용자 요청: 한눈에 비교되게).
   방금 도착(≤ 2시간) 진한 초록 · 정상(≤ TDC) 연초록 · 곧 도착(≤ TDC+1h) 하늘 · 지연(> TDC+1h) 주황 · 미수신(> 2·TDC+1h) 빨강.
   경계는 수신 등급(백엔드 _level)과 같다. 색만으로 구분하지 않도록 단계 글자 · 경과 · 다음 예정을 함께 적는다 */
const FRESH = [
  { k: 'now',  label: '방금 도착', bg: '#bbf7d0', fg: '#14532d', bar: '#16a34a' },
  { k: 'ok',   label: '정상',      bg: '#ecfdf5', fg: '#166534', bar: '#4ade80' },
  { k: 'due',  label: '곧 도착', bg: '#e0f2fe', fg: '#075985', bar: '#38bdf8' },
  { k: 'late', label: '지연',    bg: '#ffedd5', fg: '#9a3412', bar: '#f97316' },
  { k: 'lost', label: '미수신',  bg: '#fee2e2', fg: '#991b1b', bar: '#ef4444' },
];
const FreshCell: React.FC<{ at?: string; ageH?: number | null; tdcSec?: number }> = ({ at, ageH, tdcSec }) => {
  if (!at || ageH == null) return <span style={{ display: 'block', padding: '4px 8px', borderRadius: 6, background: gray[100], color: gray[500], fontSize: 12 }}>수신 없음</span>;
  const tdcH = (tdcSec || 86400) / 3600;
  // '방금' 은 2시간 이내만 — 1일 1회 주기에서 '주기의 절반' 을 방금으로 잡으면 10시간 전 업로드가 방금으로 보였다(2026-11-04 검토)
  const f = ageH <= Math.min(2, tdcH / 2) ? FRESH[0] : ageH <= tdcH ? FRESH[1] : ageH <= tdcH + 1 ? FRESH[2] : ageH <= 2 * tdcH + 1 ? FRESH[3] : FRESH[4];
  const next = dayjs(at).add(tdcH, 'hour');
  const pct = Math.min(100, Math.round((ageH / tdcH) * 100));
  return (
    <Tooltip title={<div style={{ fontSize: 12 }}>마지막 업로드 {fmtDateTime(at)}<br />주기 {tdcLabel(tdcSec)} · 다음 예정 {next.format('MM-DD HH:mm')}<br />
      {f.label} — 주기의 {Math.round((ageH / tdcH) * 100)}% 경과</div>}>
      {/* 한 줄로 — 시각 · 경과 · 단계 글자. 다음 예정은 툴팁, 주기 진행은 아래 막대(2px)로. 두 줄 + 막대였을 때 행이 93px 라 1366 에서 4행만 보였다(2026-11-05) */}
      <div style={{ width: 168, boxSizing: 'border-box', background: f.bg, color: f.fg, borderRadius: 5, padding: '1px 7px 2px', borderLeft: `3px solid ${f.bar}`, fontVariantNumeric: 'tabular-nums', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, fontSize: 12, whiteSpace: 'nowrap' }}>
          <b>{dayjs(at).format('MM-DD HH:mm')}</b>
          <span style={{ fontSize: 11, opacity: 0.85 }}>{ageH < 1 ? `${Math.round(ageH * 60)}분` : `${ageH.toFixed(1)}h`}</span>
          <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 'auto' }}>{f.label}</span>
        </div>
        <div style={{ position: 'absolute', left: 0, bottom: 0, height: 2, width: `${pct}%`, background: f.bar }} />
      </div>
    </Tooltip>
  );
};

/* ────────────────────────── 장치 대장 탭 ────────────────────────── */
type Cmd = { k: 'newPoint' | 'newDevice'; imei?: string; at: number } | null;

/* IMEI 검사 숫자(Luhn) — 라벨을 옮겨 적다 한 자리를 틀리면 대부분 여기서 걸린다. 경고만 하고 막지는 않는다 */
const imeiLuhnOk = (v: string) => {
  if (!/^\d{15}$/.test(v)) return true;
  let t = 0;
  for (let i = 0; i < 14; i++) { let d = +v[13 - i]; if (i % 2 === 0) { d *= 2; if (d > 9) d -= 9; } t += d; }
  return (10 - (t % 10)) % 10 === +v[14];
};
const isDummy = (d: any) => String(d?.note || '').startsWith('[더미]');

const DevicesTab: React.FC<{ devices: any[]; loading: boolean; reload: () => void; picked: string | null; cmd?: Cmd }> = ({ devices, loading, reload, picked, cmd }) => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('thlogger', 'WRITE');
  const canDelete = hasPermission('thlogger', 'DELETE');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState({ page: 1, size: 20 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [cfgFor, setCfgFor] = useState<any | null>(null);
  // 최근 수신 띠 — 장치 목록이 다시 오면(2분 주기 · 저장 후) 같이 다시 받는다
  const [rx, setRx] = useState<{ now: string; items: Record<string, any[]> } | null>(null);
  useEffect(() => { thloggerAPI.uplinkHistory(14).then(r => setRx(r.data)).catch(() => {}); }, [devices]);
  const rxOf = useCallback((d: any) => summarize(rx?.items?.[d.id], d.tdc_sec, rx?.now || new Date().toISOString(), 14), [rx]);
  const [cfgForm] = Form.useForm();
  const cfgTdc = Form.useWatch('tdc_hours', cfgForm);
  const cfgN = Form.useWatch('n_entries', cfgForm);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [assignForm] = Form.useForm();
  const [schedOpts, setSchedOpts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);


  const [issueF, setIssueF] = useState<'all' | 'issue' | 'crit'>('all');
  const rowsAll = useMemo(() => devices.filter(d =>
    (showInactive || d.is_active) &&
    (!picked || d.level === picked) &&
    (!search || [d.name, d.imei, d.current_point?.code, d.current_point?.name, d.current_assignment?.test_number, d.location_name].join(' ').toLowerCase().includes(search.toLowerCase())),
  ), [devices, showInactive, picked, search]);
  const issueOf = useCallback((d: any) => devIssues(d, rx ? summarize(rx.items?.[d.id], d.tdc_sec, rx.now, 14) : null), [rx]);
  // 기본 정렬 = 이상 장치가 위 (심각 → 주의 → 정상, 같은 등급은 지점 코드 순)
  const rows = useMemo(() => {
    const rank = (d: any) => { const i = issueOf(d); return !d.is_active ? 3 : i.lv === 'crit' ? 0 : i.lv === 'warn' ? 1 : 2; };
    const code = (d: any) => d.current_point?.code || d.name;
    return rowsAll.filter(d => issueF === 'all' || (issueF === 'crit' ? issueOf(d).lv === 'crit' : !!issueOf(d).lv))
      .sort((a, b) => rank(a) - rank(b) || String(code(a)).localeCompare(String(code(b)), 'ko', { numeric: true }));
  }, [rowsAll, issueF, issueOf]);
  const issueCounts = useMemo(() => {
    const act = rowsAll.filter(d => d.is_active);
    const all = act.map(issueOf);
    const has = (t: string) => all.filter(i => i.reasons.some(r => r.startsWith(t))).length;
    return { issue: all.filter(i => i.lv).length, crit: all.filter(i => i.lv === 'crit').length,
      chips: [['미수신', has('미수신')], ['수신 지연', has('수신 지연')], ['누락', has('누락')], ['지연', has('지연 ')], ['배터리', has('배터리')], ['신호 약함', has('신호')]] as [string, number][] };
  }, [rowsAll, issueOf]);

  const openCreate = (imei?: string) => { setEditing(null); form.resetFields(); form.setFieldsValue({ imei, model: 'S31-CB', tr_min: 60, tdc_sec: 86400, n_entries: 32, sim_carrier: '1NCE', is_active: true }); setFormOpen(true); };
  // 시작 단계 · 거절 로그의 '등록' 에서 넘어온 요청 — 등록 창을 연다(IMEI 미리 채움)
  useEffect(() => { if (cmd?.k === 'newDevice') openCreate(cmd.imei); }, [cmd?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = (d: any) => {
    setEditing(d);
    form.setFieldsValue({ ...d, installed_at: d.installed_at ? dayjs(d.installed_at) : null });
    setFormOpen(true);
  };
  const submit = async () => {
    const v = await form.validateFields();
    const payload = { ...v, imei: v.imei ? String(v.imei).trim() : v.imei };
    setSaving(true);
    try {
      if (editing) { await thloggerAPI.updateDevice(editing.id, payload); message.success('수정되었습니다.'); }
      else { await thloggerAPI.createDevice(payload); message.success('등록되었습니다. 이제 설치 지점 탭에서 지점에 설치하세요.'); }
      setFormOpen(false); reload();
    } catch (e: any) { message.error(e?.response?.data?.detail || '저장에 실패했습니다.'); }
    finally { setSaving(false); }
  };
  const remove = async (d: any) => {
    try {
      const { data } = await thloggerAPI.deleteDevice(d.id);
      message.success(data.message || '삭제되었습니다.'); reload();
    } catch (e: any) { message.error(e?.response?.data?.detail || '삭제에 실패했습니다.'); }
  };

  const openCfg = (d: any, rec?: { tdc: number; n: number }) => {
    setCfgFor(d);
    const base = rec ? { tdc_sec: rec.tdc, n_entries: rec.n }
      : d.cfg_pending && ['pending', 'sent'].includes(d.cfg_status) ? d.cfg_pending : { tdc_sec: d.tdc_sec, n_entries: d.n_entries || 16 };
    cfgForm.setFieldsValue({ tdc_hours: base.tdc_sec / 3600, n_entries: base.n_entries });
  };
  const submitCfg = async () => {
    const v = await cfgForm.validateFields();
    setSaving(true);
    try {
      await thloggerAPI.requestConfig(cfgFor.id, { tdc_sec: Math.round(v.tdc_hours * 3600), n_entries: v.n_entries, tr_min: cfgFor.tr_min || 60 });
      message.success('주기 변경을 예약했습니다. 다음 업링크 때 기기로 전송됩니다.');
      setCfgFor(null); reload();
    } catch (e: any) { message.error(e?.response?.data?.detail || '주기 변경 예약에 실패했습니다.'); }
    finally { setSaving(false); }
  };
  const cancelCfg = async () => {
    try { await thloggerAPI.cancelConfig(cfgFor.id); message.success('예약을 취소했습니다.'); setCfgFor(null); reload(); }
    catch (e: any) { message.error(e?.response?.data?.detail || '취소에 실패했습니다.'); }
  };

  const openAssign = async (d: any) => {
    setAssignFor(d); assignForm.resetFields(); assignForm.setFieldsValue({ valid_from: dayjs() });
    try { const { data } = await thloggerAPI.assignments(d.id); setAssignments(data || []); } catch { setAssignments([]); }
  };
  const searchSchedules = useMemo(() => {
    let t: any;
    return (q: string) => {
      clearTimeout(t);
      t = setTimeout(() => {
        scheduleAPI.list({ search: q, page: 1, page_size: 30 }).then(r => {
          const items = r.data?.items || r.data || [];
          setSchedOpts(items.map((s: any) => ({ value: s.id, label: `${s.test_number} · ${s.title || ''}` })));
        }).catch(() => setSchedOpts([]));
      }, 300);
    };
  }, []);
  const submitAssign = async () => {
    const v = await assignForm.validateFields();
    setSaving(true);
    try {
      await thloggerAPI.createAssignment(assignFor.id, {
        schedule_id: v.schedule_id, test_number: v.test_number,
        valid_from: v.valid_from.format('YYYY-MM-DDT00:00:00+09:00'),
        valid_to: v.valid_to ? v.valid_to.format('YYYY-MM-DDT23:59:59+09:00') : null, note: v.note,
      });
      message.success('배정되었습니다.');
      const { data } = await thloggerAPI.assignments(assignFor.id); setAssignments(data || []); reload();
      assignForm.resetFields(); assignForm.setFieldsValue({ valid_from: dayjs() });
    } catch (e: any) { message.error(e?.response?.data?.detail || '배정에 실패했습니다.'); }
    finally { setSaving(false); }
  };

  // 열을 함부로 늘리지 않는다 — 수신 상태 · 하우스 · 시험장소는 다른 칸에 합쳤다(2026-11-04). 예외: IMEI · 망은 장치 칸 둘째 줄에 있으면 이상 행이 3줄이 돼 별도 열로 뺐다(2026-11-05)
  const columns: any[] = [
    { title: '설치 지점', key: 'point', width: 116, render: (_: any, r: any) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        <CardStylePicker point={r.current_point} canWrite={canWrite} onSaved={reload} />
        {r.current_point
          ? <Tooltip title={`${r.current_point.name} · ${fmtDate(r.current_point.since)}~${r.location_name ? ` · ${r.location_name}` : ''}`}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{r.current_point.code}</b></Tooltip>
          : <Text type="secondary" style={{ fontSize: 12 }}>예비</Text>}
      </div>) },
    { title: '장치 · 배정 시험', dataIndex: 'name', render: (v: string, r: any) => {
      const is = issueOf(r);
      const a = r.current_assignment;
      return (
        <div><div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <span style={{ flex: 'none' }}>{v}</span>
          {!r.is_active && <Tag style={{ margin: 0 }}>비활성</Tag>}
          {isDummy(r) && <Tooltip title="시연용 더미 기기 — 실제 운영 전 초기화 대상"><Tag color="purple" style={{ margin: 0, fontSize: 11, lineHeight: '16px', padding: '0 5px' }}>더미</Tag></Tooltip>}
          {a && <Tooltip title={a.schedule_title}><Tag color="geekblue" style={{ margin: 0, fontSize: 11, lineHeight: '16px', padding: '0 5px' }}>{a.test_number}</Tag></Tooltip>}
          {/* 설치 위치 이름 — 넓은 화면에서 남는 폭을 쓰고, 좁으면 말줄임(전체는 툴팁) */}
          {r.current_point?.name && <span style={{ fontWeight: 400, fontSize: 12, color: gray[500], overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} title={r.current_point.name}>{r.current_point.name}</span>}
        </div>
          {/* 둘째 줄은 이상 이유만 — IMEI · 통신망은 옆 열로 분리해 이상 행도 2줄로 끝난다(2026-11-05) */}
          {is.reasons.length > 0 && <div title={is.reasons.join(' · ')} style={{ fontSize: 11.5, fontWeight: 700, color: is.lv === 'crit' ? '#b91c1c' : '#b45309', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {is.lv === 'crit' ? '■ ' : '▲ '}{is.reasons.join(' · ')}</div>}
        </div>);
    } },
    { title: 'IMEI · 망', dataIndex: 'imei', width: 124, render: (v: string, r: any) => (
      <div style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', lineHeight: 1.35 }}>
        <div style={{ fontSize: 11, color: gray[600], letterSpacing: -0.4 }}>{v}</div>
        <div style={{ fontSize: 11, color: gray[400] }}>{r.sim_carrier || '-'}</div>
      </div>) },
    { title: '최근 업로드 · 14일 수신', dataIndex: 'last_uplink_at', width: 252,
      sorter: (a: any, b: any) => (a.last_uplink_age_h ?? 9e9) - (b.last_uplink_age_h ?? 9e9),
      render: (v: string, r: any) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FreshCell at={v} ageH={r.last_uplink_age_h} tdcSec={r.tdc_sec} />
          {rx && <ReceptionStrip s={rxOf(r)} tdcSec={r.tdc_sec} />}
        </div>) },
    { title: '최근 측정', key: 'last', width: 150, render: (_: any, r: any) => r.last_reading_ts
      ? <div style={{ whiteSpace: 'nowrap' }}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{Number(r.last_temp_c).toFixed(1)}℃ · {Math.round(r.last_humidity)}%</span>
          <div style={{ fontSize: 11, color: gray[400] }}>{dayjs(r.last_reading_ts).format('MM-DD HH:mm')}</div></div>
      : <Text type="secondary">-</Text> },
    { title: '배터리 · 신호', key: 'bs', width: 156, sorter: (a: any, b: any) => (a.last_battery_v ?? 9) - (b.last_battery_v ?? 9),
      render: (_: any, r: any) => <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <BatteryBadge v={r.last_battery_v} compact label /><SignalBadge dbm={r.last_signal_dbm} compact label /></div> },
    { title: '최적화', key: 'opt', width: 144,
      sorter: (a: any, b: any) => ['action', 'improve', 'best'].indexOf(evaluateOptimization(a).grade) - ['action', 'improve', 'best'].indexOf(evaluateOptimization(b).grade),
      render: (_: any, r: any) => <OptimizationBadge d={r} onFix={canWrite ? (tdc, n) => openCfg(r, { tdc, n }) : undefined} /> },
    // 주기: 내용 '1일 1회 · N32' 66px — 상태 태그는 라벨만(요청값은 툴팁), 폭 82 (2026-11-05 실측)
    { title: '주기', key: 'period', width: 82, render: (_: any, r: any) => {
      const st = CFG_STATUS[r.cfg_status];
      const p = r.cfg_pending;
      return (
        <div style={{ fontSize: 12 }}>
          <Tooltip title={`측정 ${r.tr_min}분 · 업로드 ${tdcLabel(r.tdc_sec)} · 동봉 ${r.n_entries ?? '-'}건`}>
            <div style={{ whiteSpace: 'nowrap' }}>{tdcLabel(r.tdc_sec)} · N{r.n_entries ?? '-'}</div></Tooltip>
          {st && r.cfg_status !== 'cancelled' && (
            <Tooltip title={<div>{st.desc}{p && <div>요청: {tdcLabel(p.tdc_sec)} · N {p.n_entries}</div>}
              {r.cfg_attempts ? <div>전송 {r.cfg_attempts}회</div> : null}{r.cfg_note && <div>{r.cfg_note}</div>}
              {r.cfg_requested_at && <div>요청 {fmtDateTime(r.cfg_requested_at)} · {r.cfg_requested_by}</div>}</div>}>
              <Tag color={st.color} style={{ margin: '2px 0 0' }}>{st.label}</Tag>
            </Tooltip>
          )}
        </div>
      );
    } },
    { title: '작업', key: 'actions', width: canDelete ? 112 : 88, fixed: 'right' as const, render: (_: any, r: any) => (
      <Space size={2}>
        <Tooltip title="시험 배정"><Button type="text" size="small" icon={<LinkOutlined />} onClick={() => openAssign(r)} /></Tooltip>
        {canWrite && <Tooltip title="주기 변경 (기기로 전송)"><Button type="text" size="small" icon={<SendOutlined />} onClick={() => openCfg(r)} /></Tooltip>}
        {canWrite && <Tooltip title="수정"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>}
        {canDelete && (
          <Popconfirm title="삭제하시겠습니까? 측정 이력이 있으면 비활성화됩니다." okText="삭제" cancelText="취소" onConfirm={() => remove(r)}>
            <Tooltip title="삭제/비활성"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        )}
      </Space>) },
  ];
  // 장치 칸(이름 · 태그 · 설치 위치 이름 ≈ 360px)은 그 폭까지만 늘고 넘는 폭은 작업 열 앞 간격으로 (2026-11-05)
  const devFlexW = useFlexColumn(tableWrapRef, columns.reduce((t, c) => t + (c.width || 0), 0), 120, 380);
  columns[1].width = devFlexW;
  columns.splice(columns.length - 1, 0, FLEX_SPACER);

  return (
    <div ref={cardWrapRef}>
      <Card bodyStyle={{ padding: '10px 14px 0' }}
        title={<Space><span>장치 대장</span><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>IMEI 를 등록해야 업링크가 저장됩니다 (미등록은 거절 로그로)</Text></Space>}
        extra={<Space size={8}>
          <Input.Search size="small" allowClear placeholder="장치명·IMEI·하우스·시험번호" style={{ width: 230 }} onSearch={v => { setSearch(v); setPage(p => ({ ...p, page: 1 })); }} />
          <Space size={4}><Switch size="small" checked={showInactive} onChange={setShowInactive} /><Text style={{ fontSize: 12 }}>비활성 포함</Text></Space>
          {canWrite && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>등록</Button>}
          <Tooltip title="새로고침"><Button size="small" icon={<ReloadOutlined />} onClick={reload} /></Tooltip>
        </Space>}>
        {/* 이상 장치 바로 찾기 — 필터 + 이유별 대수. 행은 심각(빨강) · 주의(주황) 막대 · 배경으로 강조되고 위로 정렬된다 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
          <Segmented size="small" value={issueF} onChange={v => { setIssueF(v as any); setPage(p => ({ ...p, page: 1 })); }} options={[
            { value: 'all', label: `전체 ${rowsAll.filter(d => d.is_active).length}` },
            { value: 'issue', label: <span style={{ color: issueCounts.issue ? '#b45309' : undefined, fontWeight: 700 }}>▲ 이상 {issueCounts.issue}</span> },
            { value: 'crit', label: <span style={{ color: issueCounts.crit ? '#b91c1c' : undefined, fontWeight: 700 }}>■ 심각 {issueCounts.crit}</span> },
          ]} />
          <Space size={6} wrap>
            {issueCounts.chips.filter(([, n]) => n > 0).map(([k, n]) => <Tag key={k} style={{ margin: 0 }} color={k === '미수신' ? 'red' : 'orange'}>{k} {n}</Tag>)}
            {issueCounts.issue === 0 && <Tag color="green" style={{ margin: 0 }}>이상 장치 없음</Tag>}
          </Space>
          <span style={{ marginLeft: 'auto' }}><ReceptionLegend /></span>
        </div>
        <div ref={tableWrapRef} className="fit-table">
          <Table dataSource={rows} columns={columns} rowKey="id" loading={loading} size="small"
            rowClassName={(r: any) => { const lv = issueOf(r).lv; return lv === 'crit' ? 'thl-row-crit' : lv === 'warn' ? 'thl-row-warn' : ''; }}
            // 열은 내용 폭으로 고정하고 남는 폭은 '장치 · 배정 시험' 한 열이 받는다(이상 이유 글자가 줄바꿈 없이 들어간다).
            // 'max-content' 였을 때 넓은 화면에서 모든 열에 여백을 똑같이 나눠 1920 에서 열마다 60~100px 가 비었다(2026-11-05 실측)
            scroll={{ x: 1256, y: bodyHeight }}
            locale={{ emptyText: <EmptyState title="등록된 장치가 없습니다" description="S31-CB 를 구입하면 라벨의 IMEI 15자리로 등록하세요. 설정 가이드 탭에 AT 명령이 있습니다." size="sm" /> }}
            pagination={pagination(page.page, page.size, rows.length, (p, s) => setPage({ page: p, size: s }))} />
        </div>
      </Card>

      <Modal open={formOpen} title={editing ? '장치 수정' : '장치 등록'} onCancel={() => setFormOpen(false)} onOk={submit}
        okText={editing ? '수정' : '등록'} cancelText="취소" confirmLoading={saving} destroyOnClose width={560}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={12}>
            {/* 시험장소 · 설치일은 폼에서 뺐다(2026-11-05) — 위치 · 시각은 '설치 지점 → 설치' 기록이 정본이고, 기기에 따로 적으면
                기기를 옮길 때 틀린 사본이 남는다(하우스 코드 폐기와 같은 이유). 설치일은 첫 설치 때 서버가 채운다 */}
            {!editing && <Col span={24}><Alert type="info" showIcon style={{ marginBottom: 10, padding: '4px 10px', fontSize: 12 }}
              message="설치 위치 · 일시는 여기서 적지 않습니다 — 등록 후 '설치 지점 → 설치' 에서 지점과 함께 기록합니다." /></Col>}
            <Col span={12}><Form.Item name="imei" label="IMEI (15자리)" validateFirst rules={[
                { required: true, pattern: /^\d{15}$/, message: '숫자 15자리' },
                { validator: (_: any, v: string) => !editing && v && devices.some(d => d.imei === String(v).trim()) ? Promise.reject(new Error('이미 등록된 IMEI 입니다')) : Promise.resolve() },
                { warningOnly: true, validator: (_: any, v: string) => v && !imeiLuhnOk(String(v)) ? Promise.reject(new Error('검사 숫자가 맞지 않습니다 — 라벨을 다시 확인하세요')) : Promise.resolve() },
              ]}>
              <Input placeholder="단말 라벨의 IMEI" disabled={!!editing} maxLength={15} inputMode="numeric" /></Form.Item></Col>
            <Col span={12}><Form.Item name="name" label="장치명" rules={[{ required: true, message: '장치명을 입력하세요' }]} extra="지명-번호 (예: 김천 아포-04)"><Input placeholder="김천 아포-04" /></Form.Item></Col>
            <Col span={8}><Form.Item name="model" label="모델"><Select options={['S31-CB', 'S31B-CB', 'S31-LB', 'S31-NB'].map(v => ({ value: v, label: v }))} /></Form.Item></Col>
            <Col span={8}><Form.Item name="tr_min" label="측정 주기(분)" rules={[{ required: true }]}><InputNumber min={1} max={1440} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="tdc_sec" label="업링크 주기" rules={[{ required: true }]} extra={editing ? '서버 기록만 바뀐다 — 기기 설정은 작업 열의 전송 버튼' : undefined}>
              <Select options={[{ value: 86400, label: '1일 1회 (24h · 적용 기준)' }, { value: 28800, label: '1일 3회 (8h)' }, { value: 43200, label: '1일 2회 (12h)' }, { value: 21600, label: '1일 4회 (6h)' }, { value: 7200, label: '2시간 (시험용)' }]} /></Form.Item></Col>
            {!editing && <Col span={8}><Form.Item name="n_entries" label="동봉 건수 N" extra="CLOCKLOG 의 N (최대 32)"><InputNumber min={1} max={32} style={{ width: '100%' }} /></Form.Item></Col>}
            <Col span={8}><Form.Item name="sim_carrier" label="통신사/망"><Select allowClear options={['1NCE'].map(v => ({ value: v, label: v }))} /></Form.Item></Col>
            <Col span={8}><Form.Item name="sim_no" label="SIM/ICCID"><Input /></Form.Item></Col>
            <Col span={24}><Form.Item name="note" label="비고"><Input.TextArea rows={2} /></Form.Item></Col>
            {editing && <Col span={24}><Form.Item name="is_active" label="활성 (비활성이면 업링크를 거절)" valuePropName="checked"><Switch /></Form.Item></Col>}
          </Row>
        </Form>
      </Modal>

      <Modal open={!!cfgFor} title={cfgFor ? `주기 변경 (기기로 전송) — ${cfgFor.name}` : ''} onCancel={() => setCfgFor(null)} destroyOnClose width={620}
        footer={<Space>
          {cfgFor && ['pending', 'sent', 'failed'].includes(cfgFor.cfg_status) && <Button danger onClick={cancelCfg}>예약 취소</Button>}
          <Button onClick={() => setCfgFor(null)}>닫기</Button>
          <Button type="primary" icon={<SendOutlined />} loading={saving} onClick={submitCfg}>전송 예약</Button>
        </Space>}>
        {cfgFor && (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="기기는 업로드 직후 약 3초만 수신 창을 연다. 그래서 설정은 다음 업링크의 응답에 실어 보낸다."
              description={<span style={{ fontSize: 12 }}>현재 주기({tdcLabel(cfgFor.tdc_sec)})만큼 기다려야 전달된다. 기기의 확인 응답이나 업로드 간격 변화로 반영을 확인하며,
                6회 보내도 확인이 없으면 '반영 실패'로 멈춘다. 실기기에서는 파일럿 때 이 경로를 먼저 확인한다.</span>} />
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }} items={[
              { key: 'cur', label: '현재 설정', children: `${tdcLabel(cfgFor.tdc_sec)} · N ${cfgFor.n_entries ?? '-'}` },
              { key: 'st', label: '변경 상태', children: CFG_STATUS[cfgFor.cfg_status]?.label || '없음' },
            ]} />
            <Space wrap style={{ marginBottom: 10 }}>
              {CFG_PRESETS.map(p => (
                <Button key={p.key} size="small" onClick={() => cfgForm.setFieldsValue({ tdc_hours: p.tdc / 3600, n_entries: p.n })}>{p.label}</Button>
              ))}
            </Space>
            <Form form={cfgForm} layout="inline" size="small">
              <Form.Item name="tdc_hours" label="업로드 간격(시간)" rules={[{ required: true }]}><InputNumber min={0.5} max={48} step={1} /></Form.Item>
              <Form.Item name="n_entries" label="동봉 건수 N" rules={[{ required: true }]} tooltip="업로드마다 함께 보내는 최근 측정 수. 간격의 2배면 업로드 1회 유실을 다음 업로드가 복구한다 (상한 32)">
                <InputNumber min={1} max={32} /></Form.Item>
            </Form>
            {cfgTdc > 0 && cfgN > 0 && (() => {
              const kb = estMonthKB(cfgTdc * 3600, cfgN);
              const cover = (cfgN * (cfgFor.tr_min || 60)) / 60;
              return (
                <div style={{ marginTop: 12, padding: '8px 12px', background: gray[50], borderRadius: 6, fontSize: 12.5, lineHeight: 1.9 }}>
                  <div>월 데이터 추정 <b style={{ color: (kb * 120) / 1024 > 500 ? state.errorText : gray[800] }}>{kb}KB</b> — 10년 약 {((kb * 120) / 1024).toFixed(1)}MB · 1NCE 500MB 의 {(((kb * 120) / 1024 / 500) * 100).toFixed(1)}%{(kb * 120) / 1024 > 500 ? ' (초과)' : ''}</div>
                  <div>업로드당 {cfgN}건 = {cover}시간 분량 · 간격의 {Math.round((cover / cfgTdc) * 10) / 10}배{cover < cfgTdc * 2 ? ' — 2배 미만이면 유실 시 재전송에만 의존' : ' — 유실 1회 자가 복구'}</div>
                  <div style={{ color: gray[500] }}>보낼 명령: <Text code style={{ fontSize: 11 }}>{`AT+TDC=${Math.round(cfgTdc * 3600)};AT+CLOCKLOG=1,65535,${cfgFor.tr_min || 60},${cfgN}`}</Text></div>
                </div>
              );
            })()}
          </>
        )}
      </Modal>

      <Modal open={!!assignFor} title={assignFor ? `시험 배정 — ${assignFor.name}` : ''} onCancel={() => setAssignFor(null)} footer={null} destroyOnClose width={640}>
        {canWrite && (
          <Form form={assignForm} layout="inline" size="small" style={{ marginBottom: 10, rowGap: 8 }}>
            <Form.Item name="schedule_id" label="시험일정" style={{ width: 300 }}>
              <Select showSearch allowClear placeholder="시험번호·항목명 검색" filterOption={false} onSearch={searchSchedules} options={schedOpts} notFoundContent={null} />
            </Form.Item>
            <Form.Item name="test_number" label="또는 시험번호"><Input placeholder="SR26-001" style={{ width: 120 }} /></Form.Item>
            <Form.Item name="valid_from" label="시작" rules={[{ required: true }]}><DatePicker format={DATE_FORMAT} /></Form.Item>
            <Form.Item name="valid_to" label="종료"><DatePicker format={DATE_FORMAT} placeholder="진행 중" /></Form.Item>
            <Form.Item><Button type="primary" size="small" loading={saving} onClick={submitAssign}>배정</Button></Form.Item>
          </Form>
        )}
        <Table dataSource={assignments} rowKey="id" size="small" pagination={false}
          locale={{ emptyText: <EmptyState title="배정 이력이 없습니다" size="sm" /> }}
          columns={[
            { title: '시험번호', dataIndex: 'test_number', width: 120 },
            { title: '항목명', dataIndex: 'schedule_title', ellipsis: true },
            { title: '기간', key: 'period', width: 220, render: (_: any, a: any) => `${fmtDate(a.valid_from)} ~ ${a.valid_to ? fmtDate(a.valid_to) : '진행 중'}` },
            { title: '', key: 'act', width: 80, render: (_: any, a: any) => canWrite && (
              <Space size={0}>
                {!a.valid_to && <Tooltip title="오늘로 종료"><Button type="text" size="small" icon={<StopOutlined />} onClick={async () => { await thloggerAPI.closeAssignment(a.id); const { data } = await thloggerAPI.assignments(assignFor.id); setAssignments(data || []); reload(); }} /></Tooltip>}
                <Popconfirm title="배정 이력을 삭제하시겠습니까?" okText="삭제" cancelText="취소" onConfirm={async () => { await thloggerAPI.deleteAssignment(a.id); const { data } = await thloggerAPI.assignments(assignFor.id); setAssignments(data || []); reload(); }}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space>) },
          ]} />
      </Modal>
    </div>
  );
};

/* ────────────────────────── 측정 데이터 탭 ────────────────────────── */
/* 조회 기간 프리셋 — 실제로 자주 보는 구간 (2026-11-04 검토)
   · 최근 24시간: 아침 · 퇴근 전 확인 (밤사이 최저 · 한낮 최고)
   · 최근 7일: 주간 점검 · 방제 전후 비교
   · 최근 30일: 월간 점검 · 배터리 · 결손 확인
   · 최근 3개월(기본): 약효 · 약해 시험 한 작기(보통 4~12주)를 통째로 덮는다 → 성적서 환경조건 확인에 가장 많이 쓰는 구간
   · 배정 시험 기간: 지점에 배정된 시험의 시작 ~ 종료(진행 중이면 오늘) — 성적서 첨부 엑셀과 같은 구간
   자동 집계: 14일까지 원자료 · 45일까지 1시간 평균 · 그 이상(3개월 기본)은 일 평균 + 일 최저~최고 띠 —
   3개월을 시간 단위로 그리면 일교차 톱니 2,000여 개에 추세가 묻혔다(2026-11-04 화면 실측). 세부는 기간을 줄여 본다 */
const RANGE_PRESETS = () => [
  { label: '최근 24시간', value: [dayjs().subtract(1, 'day'), dayjs()] as [Dayjs, Dayjs] },
  { label: '최근 7일', value: [dayjs().subtract(7, 'day'), dayjs()] as [Dayjs, Dayjs] },
  { label: '최근 30일', value: [dayjs().subtract(30, 'day'), dayjs()] as [Dayjs, Dayjs] },
  { label: '최근 3개월', value: [dayjs().subtract(3, 'month'), dayjs()] as [Dayjs, Dayjs] },
  { label: '올해', value: [dayjs().startOf('year'), dayjs()] as [Dayjs, Dayjs] },
];
const T_COLOR = '#e5484d';
const H_COLOR = '#2a78d6';

/* 측정 데이터 왼쪽 목록 — 셀렉트 박스 대신 (2026-11-04 사용자 요청).
   20곳을 한눈에 훑고 한 번에 고른다: 코드 · 이름 · 지금 온습도 · 수신 상태(색 점 + 정상이 아니면 글자).
   검색은 코드 · 이름 · 기기 · 시험번호. ↑↓ 키로 이웃 지점을 넘겨 보며 비교할 수 있다 */
const PICK_LV: Record<string, { c: string; t?: string }> = {
  OK: { c: '#1baf7a' }, WARN: { c: '#eda100', t: '지연' }, CRIT: { c: '#e5484d', t: '미수신' }, NEW: { c: '#94a3b8', t: '대기' },
};
const PickList: React.FC<{ kind: 'point' | 'device'; points: any[]; devices: any[]; selected?: number; onPick: (id: number) => void }> = ({ kind, points, devices, selected, onPick }) => {
  const [q, setQ] = useState('');
  const items = useMemo(() => {
    const src = kind === 'point'
      // 현황 카드와 같은 배경 · 타이틀 바 색, 같은 순서(배치 순 = 서버 sort_order) — 두 화면에서 같은 지점이 같은 모양으로 보이게
      ? points.map((p, i) => ({ id: p.id, code: p.code, name: p.name, d: p.device, off: !p.is_active, none: !p.current, ord: i,
          color: p.color || '', bar: p.bar_color || '',
          sub: p.current ? p.current.device_name : '기기 없음', key: [p.code, p.name, p.current?.device_name, p.current_assignment?.test_number].join(' ') }))
      : devices.map((d, i) => ({ id: d.id, code: d.current_point?.code || '', name: d.name, d, off: !d.is_active, none: false, ord: i,
          color: d.current_point?.color || '', bar: d.current_point?.bar_color || '',
          sub: d.current_point ? d.current_point.code : '예비 · 미설치', key: [d.name, d.imei, d.current_point?.code, d.current_point?.name].join(' ') }));
    const kw = q.trim().toLowerCase();
    return src.filter(x => !kw || x.key.toLowerCase().includes(kw))
      .sort((a, b) => Number(a.off) - Number(b.off) || (kind === 'point' ? a.ord - b.ord
        : String(a.code || a.name).localeCompare(String(b.code || b.name), 'ko', { numeric: true })));
  }, [kind, points, devices, q]);
  const move = (dir: number) => {
    const i = items.findIndex(x => x.id === selected);
    const n = items[Math.min(items.length - 1, Math.max(0, i + dir))];
    if (n) onPick(n.id);
  };
  return (
    <div className="thl-picklist" style={{ width: 236, flex: 'none', display: 'flex', flexDirection: 'column', border: `1px solid ${gray[200]}`, borderRadius: 8, overflow: 'hidden' }}
      onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); move(1); } else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); } }}>
      <div style={{ padding: 6, borderBottom: `1px solid ${gray[100]}`, background: gray[50] }}>
        <Input size="small" allowClear placeholder={kind === 'point' ? '지점 · 기기 · 시험번호' : '기기 · IMEI · 지점'} value={q} onChange={e => setQ(e.target.value)} />
        <div style={{ fontSize: 11, color: gray[400], marginTop: 4 }}>{items.length}{kind === 'point' ? '곳' : '대'} · ↑↓ 로 이동</div>
      </div>
      <div role="listbox" tabIndex={0} style={{ flex: 1, overflowY: 'auto', outline: 'none' }}>
        {items.map(x => {
          const on = x.id === selected;
          const lv = x.none ? { c: '#94a3b8', t: '기기 없음' } : (PICK_LV[x.d?.level] || PICK_LV.NEW);
          return (
            <div key={x.id} role="option" aria-selected={on} onClick={() => onPick(x.id)} className="thl-pick"
              ref={el => { if (on && el) el.scrollIntoView({ block: 'nearest' }); }}
              style={{ padding: '6px 10px', cursor: 'pointer', borderLeft: `4px solid ${on ? '#4f46e5' : x.bar || 'transparent'}`,
                // 배경 = 현황 카드 배경색. 선택은 배경을 바꾸지 않고 인디고 막대 · 안쪽 테두리 · 굵은 글자로 (지정 색이 가려지지 않게)
                background: x.color || (on ? '#eef2ff' : '#fff'), boxShadow: on ? 'inset 0 0 0 2px #4f46e5' : undefined,
                opacity: x.off ? 0.5 : 1, borderBottom: `1px solid ${x.color ? 'rgba(15,23,42,.06)' : gray[50]}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 4, background: lv.c, flex: 'none' }} />
                <b style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', flex: 'none',
                  ...(x.bar ? { background: x.bar, color: '#fff', borderRadius: 4, padding: '0 5px', lineHeight: '17px' } : { color: on ? '#3730a3' : gray[800] }) }}>{x.code || x.name}</b>
                {x.code && <span style={{ fontSize: 12, color: on ? gray[900] : gray[600], fontWeight: on ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{x.name}</span>}
                {lv.t && <span style={{ fontSize: 11, fontWeight: 700, color: lv.c, flex: 'none' }}>{lv.t}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: gray[500], marginTop: 1, paddingLeft: 13 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.sub}{x.off ? ' · 비활성' : ''}</span>
                {!x.none && x.d?.last_temp_c != null && <span style={{ fontVariantNumeric: 'tabular-nums', flex: 'none', marginLeft: 6 }}>
                  <span style={{ color: T_COLOR }}>{Number(x.d.last_temp_c).toFixed(1)}℃</span> · <span style={{ color: H_COLOR }}>{Number(x.d.last_humidity).toFixed(0)}%</span></span>}
              </div>
            </div>);
        })}
        {!items.length && <div style={{ padding: 16, fontSize: 12, color: gray[400], textAlign: 'center' }}>검색 결과 없음</div>}
      </div>
    </div>
  );
};

const DataTab: React.FC<{ devices: any[]; points: any[]; focus?: { kind: 'point' | 'device'; id: number; at: number } }> = ({ devices, points, focus }) => {
  const { hasPermission } = usePermission();
  const canExport = hasPermission('thlogger', 'EXPORT');
  const { ref: fillRef, height: fillHeight } = useFillHeight(260);
  const hasPoints = points.some(p => p.is_active);
  const [kindSel, setKind] = useState<'point' | 'device'>('point');
  // 지점이 없으면 기기 모드 — 상태로 바꿔 두면 지점 목록이 오기 전 첫 렌더에서 기기로 굳어 버린다(2026-11-04 실측)
  const kind: 'point' | 'device' = hasPoints ? kindSel : 'device';
  const [pointId, setPointId] = useState<number | undefined>();
  const [deviceId, setDeviceId] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(3, 'month'), dayjs()]);
  const [bucket, setBucket] = useState('auto');
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!pointId && points.length) setPointId(points.find(p => p.is_active)?.id || points[0].id); }, [points, pointId]);
  useEffect(() => { if (!deviceId && devices.length) setDeviceId(devices.find(d => d.is_active)?.id || devices[0].id); }, [devices, deviceId]);
  // 현황 카드 · 지점 표에서 넘어오면 그 대상을 연다 (at 으로 같은 대상을 다시 눌러도 반응)
  useEffect(() => {
    if (!focus) return;
    setKind(focus.kind);
    if (focus.kind === 'point') setPointId(focus.id); else setDeviceId(focus.id);
  }, [focus]);

  const targetId = kind === 'point' ? pointId : deviceId;
  const point = points.find(p => p.id === pointId);
  const load = useCallback(() => {
    if (!targetId) return;
    setLoading(true);
    thloggerAPI.readings({ [kind === 'point' ? 'point_id' : 'device_id']: targetId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD'), bucket } as any)
      .then(r => setData(r.data)).catch(() => message.error('측정 데이터를 불러오지 못했습니다.')).finally(() => setLoading(false));
  }, [kind, targetId, range, bucket]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    if (!targetId) return;
    try {
      const res = await thloggerAPI.exportExcel({ [kind === 'point' ? 'point_id' : 'device_id']: targetId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') } as any);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const tag = kind === 'point' ? point?.code : devices.find(d => d.id === deviceId)?.imei;
      a.href = url; a.download = `thlogger_${tag || targetId}_${range[0].format('YYYYMMDD')}-${range[1].format('YYYYMMDD')}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      message.success('엑셀 파일을 내려받았습니다.');
    } catch { message.error('엑셀 내보내기에 실패했습니다.'); }
  };

  // 배정 시험 기간 프리셋 — 지점(또는 장치)에 지금 배정된 시험
  const asg = kind === 'point' ? point?.current_assignment : devices.find(d => d.id === deviceId)?.current_assignment;
  const presets = [
    ...RANGE_PRESETS(),
    ...(asg ? [{ label: `시험 기간 (${asg.test_number})`, value: [dayjs(asg.valid_from), asg.valid_to ? dayjs(asg.valid_to) : dayjs()] as [Dayjs, Dayjs] }] : []),
  ];

  const segs: any[] = useMemo(() => (data?.kind === 'point' ? (data.segments || []) : []), [data]);
  const option = useMemo(() => {
    const s = data?.series || [];
    const isAgg = data?.bucket && data.bucket !== 'raw';
    // 기기 교체 경계 — 지점 기록이 어느 시각부터 어느 기기 값인지 차트 위에 세로선으로
    const marks = segs.filter((x, i) => i > 0 && dayjs(x.valid_from).isAfter(dayjs(data?.from)))
      .map(x => ({ xAxis: x.valid_from, label: { formatter: `→ ${x.device_name}${segs.find(y => y.valid_to === x.valid_from)?.end_reason ? ` (${segs.find(y => y.valid_to === x.valid_from)?.end_reason})` : ''}`, fontSize: 11, color: gray[700], position: 'insideEndTop' } }));
    return {
      animation: false,
      tooltip: { trigger: 'axis', valueFormatter: (v: any) => (v == null ? '-' : Number(v).toFixed(1)) },
      legend: { top: 0, data: ['온도(℃)', '습도(%)', ...(data?.bucket === '1d' && (data?.series || []).some((p: any) => (p.n ?? 24) < 20) ? ['부분 집계'] : [])] },
      grid: { left: 48, right: 48, top: 32, bottom: 56 },
      // 기간이 사흘을 넘으면 날짜만 — 'MM-DD HH:mm' 은 3개월에서 라벨끼리 겹쳤다. 겹치는 라벨은 숨긴다
      xAxis: { type: 'time', axisLabel: { hideOverlap: true, fontSize: 11,
        formatter: (v: number) => dayjs(v).format(dayjs(data?.to).diff(dayjs(data?.from), 'day') > 3 ? 'MM-DD' : 'MM-DD HH:mm') } },
      yAxis: [
        { type: 'value', name: '℃', nameTextStyle: { color: T_COLOR }, axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { color: gray[100] } } },
        { type: 'value', name: '%RH', min: 0, max: 100, nameTextStyle: { color: H_COLOR }, axisLabel: { fontSize: 11 }, splitLine: { show: false } },
      ],
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
      series: [
        { name: '온도(℃)', type: 'line', showSymbol: false, smooth: false, yAxisIndex: 0, color: T_COLOR, lineStyle: { width: 1.5 },
          data: s.map((p: any) => [p.ts, p.temp_c]),
          ...(marks.length ? { markLine: { symbol: 'none', silent: true, lineStyle: { color: '#6366f1', type: 'dashed', width: 1.2 }, data: marks } } : {}) },
        ...(isAgg ? [{ name: '온도 범위', type: 'line', yAxisIndex: 0, showSymbol: false, lineStyle: { opacity: 0 }, stack: 'tr', color: 'transparent', data: s.map((p: any) => [p.ts, p.temp_min]) },
          { name: '온도 범위', type: 'line', yAxisIndex: 0, showSymbol: false, lineStyle: { opacity: 0 }, stack: 'tr', color: T_COLOR, areaStyle: { opacity: 0.12 }, data: s.map((p: any) => [p.ts, (p.temp_max ?? 0) - (p.temp_min ?? 0)]) }] : []),
        { name: '습도(%)', type: 'line', showSymbol: false, yAxisIndex: 1, color: H_COLOR, lineStyle: { width: 1.5 }, data: s.map((p: any) => [p.ts, p.humidity]) },
        // 일 평균에서 측정이 20건 미만인 날(오늘 · 설치일 · 고장일)은 '부분 집계' — 평균이 하루 전체를 대표하지 않는다(오늘 점이 뚝 떨어져 보였다)
        ...(data?.bucket === '1d' ? [{ name: '부분 집계', type: 'scatter', yAxisIndex: 0, symbol: 'emptyCircle', symbolSize: 8, color: T_COLOR, z: 5,
          tooltip: { valueFormatter: (v: any) => (v == null ? '-' : Number(v).toFixed(1)) },
          data: s.filter((p: any) => (p.n ?? 24) < 20).map((p: any) => ({ value: [p.ts, p.temp_c], n: p.n })) }] : []),
      ],
    };
  }, [data, segs]);

  const st = data?.stats;
  const statItem = (label: string, v: any, unit = '', tone?: string) => (
    <div style={{ background: gray[50], borderRadius: 6, padding: '5px 10px', minWidth: 88 }}>
      <div style={{ fontSize: 11, color: gray[500] }}>{tone && <span style={{ display: 'inline-block', width: 8, height: 2, background: tone, verticalAlign: 'middle', marginRight: 4 }} />}{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v == null ? '-' : `${v}${unit}`}</div>
    </div>);

  const empty = kind === 'point' ? !points.length : !devices.length;
  return (
    // 본문 하단 패딩 0 — 목록 · 차트가 useFillHeight 로 뷰포트 끝까지 차므로 카드 패딩만큼 문서 스크롤이 생긴다(실측 11px)
    <Card bodyStyle={{ padding: '10px 14px 0' }}
      title={<Space size={8}>
        <span>측정 데이터</span>
        {hasPoints && <Segmented size="small" value={kind} onChange={v => setKind(v as any)} options={[{ value: 'point', label: '지점' }, { value: 'device', label: '기기' }]} />}
      </Space>}
      extra={<Space size={8} wrap>
        <RangePicker size="small" value={range} format={DATE_FORMAT} allowClear={false} onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])} presets={presets} />
        <Select size="small" style={{ width: 112 }} value={bucket} onChange={setBucket}
          options={[{ value: 'auto', label: '자동 집계' }, { value: 'raw', label: '원자료' }, { value: '1h', label: '1시간 평균' }, { value: '1d', label: '일 평균' }]} />
        {canExport && <Button size="small" icon={<DownloadOutlined />} onClick={exportExcel} disabled={!targetId}>엑셀</Button>}
        <Tooltip title="새로고침"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
      </Space>}>
      {/* 높이 측정 상자는 항상 그린다 — 데이터가 온 뒤에야 붙으면 useFillHeight 가 다시 재지 못해 목록 길이만큼 문서가 늘어난다(834px 실측) */}
      <div ref={fillRef} className="thl-data-body" style={{ height: fillHeight ?? 300, display: 'flex', gap: 12, minWidth: 0 }}>
      {empty ? <div style={{ flex: 1 }}><EmptyState title={kind === 'point' ? '지점이 없습니다' : '장치가 없습니다'} description={kind === 'point' ? '설치 지점 탭에서 지점을 등록하세요.' : '장치 대장에서 먼저 등록하세요.'} /></div> : (
        <>
          <PickList kind={kind} points={points} devices={devices} selected={targetId}
            onPick={id => (kind === 'point' ? setPointId(id) : setDeviceId(id))} />
          {/* overflow hidden — 차트 캔버스가 높이 재계산보다 늦게 줄어드는 순간 문서가 늘어나는 것을 막는다(59px 실측) */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Space size={6} wrap style={{ marginBottom: 6 }}>
            {statItem('측정 건수', st?.count)}
            {statItem('온도 평균', st?.temp_avg, '℃', T_COLOR)}{statItem('온도 최저', st?.temp_min, '℃', T_COLOR)}{statItem('온도 최고', st?.temp_max, '℃', T_COLOR)}
            {statItem('습도 평균', st?.hum_avg, '%', H_COLOR)}{statItem('습도 최저', st?.hum_min, '%', H_COLOR)}{statItem('습도 최고', st?.hum_max, '%', H_COLOR)}
            {statItem('결손(정시)', st?.gaps, st?.gaps ? '건' : '')}
            {data?.bucket && <Tag style={{ alignSelf: 'center' }}>{data.bucket === 'raw' ? '원자료' : data.bucket === '1h' ? '1시간 평균' : '일 평균'} · {range[1].diff(range[0], 'day')}일</Tag>}
          </Space>
          {kind === 'point' && segs.length > 0 && (
            <div style={{ fontSize: 12, color: gray[600], marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ color: gray[500] }}>사용 기기</span>
              {segs.map((x, i) => (
                <React.Fragment key={x.id}>
                  {i > 0 && <span style={{ color: '#6366f1' }}>→</span>}
                  <Tooltip title={<div>{x.imei}<br />{fmtDateTime(x.valid_from)} ~ {x.valid_to ? fmtDateTime(x.valid_to) : '현재'}{x.end_reason && <><br />내린 사유: {x.end_reason}</>}{x.check_note && <><br />확인: {x.check_note}</>}</div>}>
                    <Tag style={{ margin: 0 }}>{x.device_name} <span style={{ color: gray[400] }}>{dayjs(x.valid_from).format('MM-DD')}~{x.valid_to ? dayjs(x.valid_to).format('MM-DD') : '현재'}</span></Tag>
                  </Tooltip>
                </React.Fragment>))}
              {segs.length > 1 && <Text type="secondary" style={{ fontSize: 11.5 }}>점선 = 기기 교체 시각 · 지점 기록은 이어진다</Text>}
            </div>)}
          {kind === 'point' && point && !point.current && <Alert type="warning" showIcon style={{ marginBottom: 6 }} message="이 지점에는 지금 설치된 기기가 없습니다 — 기록이 멈춰 있습니다." />}
          {st && st.count === 0 && <Alert type="info" showIcon style={{ marginBottom: 6 }} message="선택한 기간에 측정값이 없습니다." />}
          <div className="thl-data-chart" style={{ flex: 1, minHeight: 120, position: 'relative', overflow: 'hidden' }}>
            <ReactECharts option={option} style={{ position: 'absolute', inset: 0, height: '100%' }} showLoading={loading} notMerge />
          </div>
          </div>
        </>
      )}
      </div>
    </Card>
  );
};

/* ────────────────────────── 업링크 로그 탭 ────────────────────────── */
const UplinksTab: React.FC<{ devices: any[] }> = ({ devices }) => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [deviceId, setDeviceId] = useState<number | undefined>();
  const [page, setPage] = useState({ page: 1, size: 20 });
  const [data, setData] = useState<{ items: any[]; total: number }>({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    thloggerAPI.uplinks({ device_id: deviceId, page: page.page, page_size: page.size })
      .then(r => setData(r.data)).catch(() => message.error('업링크 로그를 불러오지 못했습니다.')).finally(() => setLoading(false));
  }, [deviceId, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div ref={cardWrapRef}>
      <Card bodyStyle={{ padding: '10px 14px 0' }} title={<Space><span>업링크 로그</span><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>단말이 서버에 접속한 기록 — 저장 0건이어도 수신은 수신</Text></Space>}
        extra={<Space size={8}>
          <Select size="small" allowClear style={{ width: 200 }} placeholder="전체 장치" value={deviceId} onChange={v => { setDeviceId(v); setPage(p => ({ ...p, page: 1 })); }}
            options={devices.map(d => ({ value: d.id, label: `${d.current_point?.code ? d.current_point.code + ' · ' : ''}${d.name}` }))} />
          <Tooltip title="새로고침"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        </Space>}>
        <div ref={tableWrapRef} className="fit-table">
          <Table dataSource={data.items} rowKey="id" size="small" loading={loading} scroll={{ x: 1090, y: bodyHeight }}
            locale={{ emptyText: <EmptyState title="업링크가 없습니다" size="sm" /> }}
            expandable={{ expandedRowRender: (r: any) => <UplinkDetail r={r} />, rowExpandable: (r: any) => !!r.raw }}
            columns={[
              { title: '수신 시각', dataIndex: 'received_at', width: 132, render: (v: string) => <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDateTime(v)}</span> },
              { title: '장치', dataIndex: 'device_name', width: 140, render: (v: string, r: any) => <div style={{ whiteSpace: 'nowrap' }}>{v || '-'}<div style={{ fontSize: 11, color: gray[400], fontVariantNumeric: 'tabular-nums' }}>{r.imei}</div></div> },
              { title: '형식', key: 'fmt', width: 78, render: (_: any, r: any) => r.decoded?.format
                ? <Tag style={{ margin: 0 }} color={r.decoded.format === 'hex' ? 'geekblue' : 'default'}>{r.decoded.format.toUpperCase()} {r.raw_len || r.decoded.bytes}B</Tag> : '-' },
              { title: '측정/저장', key: 'n', width: 116, render: (_: any, r: any) => <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.n_records} / <b>{r.n_inserted}</b>{r.n_records > r.n_inserted && <Text type="secondary" style={{ fontSize: 11 }}> (중복 {r.n_records - r.n_inserted})</Text>}</span> },
              { title: '배터리', dataIndex: 'battery_v', width: 88, render: (v: number) => <BatteryBadge v={v} compact /> },
              { title: '신호', key: 'sig', width: 96, render: (_: any, r: any) => <SignalBadge dbm={r.signal_dbm} csq={r.signal_csq} compact /> },
              { title: '전송', key: 'peer', width: 178, render: (_: any, r: any) => <span style={{ fontSize: 12, color: gray[600], whiteSpace: 'nowrap' }}>{r.transport} · {r.peer}</span> },
              // 이 업링크가 실어 온 측정 구간(연구 기록의 '어느 시간을 덮었나')과 단말 시각. 내용 폭 고정 — 남는 폭은 끝의 간격 열이 받는다(2026-11-05, 1920 에서 792px 편중)
              { title: '동봉 구간 · 단말 시각', key: 'span', width: 238, render: (_: any, r: any) => {
                const ts = ((r.decoded?.records || []) as any[]).filter(x => x.source === 'log').map(x => x.ts).sort();
                return <div style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: 12 }}>
                  {ts.length ? <span>{dayjs(ts[0]).format('MM-DD HH:mm')} ~ {dayjs(ts[ts.length - 1]).format('MM-DD HH:mm')} <Text type="secondary" style={{ fontSize: 11 }}>· 정시 {ts.length}건</Text></span>
                    : <Text type="secondary" style={{ fontSize: 11.5 }}>측정값 없음</Text>}
                  <div style={{ fontSize: 11, color: gray[400] }}>단말 {r.device_time ? fmtDateTime(r.device_time) : '-'}</div></div>;
              } },
              FLEX_SPACER,
            ]}
            pagination={pagination(page.page, page.size, data.total, (p, s) => setPage({ page: p, size: s }))} />
        </div>
      </Card>
    </div>
  );
};

/* ────────────────────────── 거절 로그 탭 ────────────────────────── */
const RejectsTab: React.FC<{ devices: any[]; onRegister: (imei: string) => void; onGoTab: (k: string) => void }> = ({ devices, onRegister, onGoTab }) => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('thlogger', 'WRITE');
  const [page, setPage] = useState({ page: 1, size: 20 });
  const [data, setData] = useState<{ items: any[]; total: number }>({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    thloggerAPI.rejects({ page: page.page, page_size: page.size })
      .then(r => setData(r.data)).catch(() => message.error('거절 로그를 불러오지 못했습니다.')).finally(() => setLoading(false));
  }, [page]);
  useEffect(() => { load(); }, [load]);
  // 처음 켠 기기는 미등록 IMEI 로 여기 남는다 — 같은 IMEI 가 여러 번 쌓이므로 IMEI 별로 묶어 '등록' 한 번으로 끝내게 한다(2026-11-05)
  const registered = useMemo(() => new Set(devices.map(d => d.imei)), [devices]);
  const pending = useMemo(() => {
    const m = new Map<string, { imei: string; n: number; last: string }>();
    data.items.filter(r => r.imei && r.reason?.startsWith('unregistered') && !registered.has(r.imei)).forEach(r => {
      const x = m.get(r.imei) || { imei: r.imei, n: 0, last: r.received_at }; x.n++; if (r.received_at > x.last) x.last = r.received_at; m.set(r.imei, x);
    });
    return Array.from(m.values());
  }, [data.items, registered]);
  return (
    <div ref={cardWrapRef}>
      {pending.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 8, padding: '6px 12px' }}
          message={<span style={{ fontSize: 12.5 }}>미등록 IMEI <b>{pending.length}개</b> — 기기를 처음 켰다면 정상입니다. 장치 대장에 등록하면 <b>다음 업로드부터</b> 저장됩니다.</span>}
          description={<Space wrap size={6} style={{ marginTop: 2 }}>{pending.slice(0, 6).map(x => (
            <Space key={x.imei} size={4} style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 6, padding: '1px 4px 1px 8px', fontSize: 12 }}>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>{x.imei}</b><Text type="secondary" style={{ fontSize: 11 }}>{x.n}회 · 마지막 {fmtDateTime(x.last)}</Text>
              {canWrite && <Button size="small" type="primary" onClick={() => onRegister(x.imei)}>등록</Button>}
            </Space>))}</Space>} />
      )}
      <Card bodyStyle={{ padding: '10px 14px 0' }} title={<Space><span>거절 로그</span><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>저장하지 않은 패킷 — 미등록 IMEI 는 장치 대장에 등록하면 다음 업링크부터 저장</Text></Space>}
        extra={<Space size={8}>
          {hasPermission('thlogger', 'DELETE') && data.total > 0 && (
            <Popconfirm title={`거절 이력 ${data.total}건을 모두 비우시겠습니까?`} okText="비우기" cancelText="취소" onConfirm={async () => { await thloggerAPI.clearRejects(); message.success('비웠습니다.'); load(); }}>
              <Button size="small" danger icon={<DeleteOutlined />}>모두 비우기</Button></Popconfirm>)}
          <Tooltip title="새로고침"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        </Space>}>
        <div ref={tableWrapRef} className="fit-table">
          <Table dataSource={data.items} rowKey="id" size="small" loading={loading} scroll={{ x: 990, y: bodyHeight }}
            locale={{ emptyText: <EmptyState title="거절된 패킷이 없습니다" size="sm" /> }}
            expandable={{ expandedRowRender: (r: any) => <pre style={{ margin: 0, fontFamily: font.mono, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: gray[50], padding: 8, borderRadius: 6 }}>{r.raw}</pre>, rowExpandable: (r: any) => !!r.raw }}
            columns={[
              { title: '수신 시각', dataIndex: 'received_at', width: 140, render: (v: string) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(v)}</span> },
              { title: '사유', dataIndex: 'reason', width: 104, ellipsis: true, render: (v: string) => <Tag color={v?.startsWith('unregistered') ? 'volcano' : v?.startsWith('inactive') ? 'default' : 'red'} style={{ margin: 0 }}>{v?.startsWith('unregistered') ? '미등록 IMEI' : v?.startsWith('inactive') ? '비활성 장치' : v}</Tag> },
              { title: 'IMEI', dataIndex: 'imei', width: 168, render: (v: string) => v ? <Space size={4}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span><Tooltip title="IMEI 복사"><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(v)} /></Tooltip></Space> : '-' },
              { title: '전송', key: 'peer', width: 178, render: (_: any, r: any) => <span style={{ fontSize: 12, color: gray[600], whiteSpace: 'nowrap' }}>{r.transport} · {r.peer}</span> },
              { title: '조치', key: 'act', width: 80, render: (_: any, r: any) => r.imei && r.reason?.startsWith('unregistered') && !registered.has(r.imei)
                ? (canWrite ? <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onRegister(r.imei)}>장치 등록</Button> : <Text type="secondary" style={{ fontSize: 12 }}>등록 필요</Text>)
                : r.reason?.startsWith('inactive') ? <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onGoTab('devices')}>활성화</Button>
                : r.imei && registered.has(r.imei) ? <Text type="secondary" style={{ fontSize: 12 }}>등록됨</Text> : null },
              // 남는 폭 = 원문 앞부분 — 펼치지 않아도 어떤 패킷이었는지(JSON · HEX · 잘림) 바로 보인다
              { title: '원문 (앞부분)', dataIndex: 'raw', ellipsis: true, render: (v: string) => v
                ? <span style={{ fontFamily: font.mono, fontSize: 11, color: gray[600] }}>{v.slice(0, 160)}</span> : <Text type="secondary">-</Text> },
            ]}
            pagination={pagination(page.page, page.size, data.total, (p, s) => setPage({ page: p, size: s }))} />
        </div>
      </Card>
    </div>
  );
};

/* ────────────────────────── 페이지 ────────────────────────── */
const ThLoggerPage: React.FC = () => {
  const [summary, setSummary] = useState<any | null>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [points, setPoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<string | null>(null);
  // 메뉴로 들어오면 언제나 '현황' 탭부터(2026-11-05 사용자 요청) — 마지막 탭을 기억하지 않는다.
  // 열려 있는 화면 탭 사이를 오갈 때는 페이지가 마운트된 채라 보던 탭이 유지된다
  const [tab, setTab] = useState('overview');
  const [focus, setFocus] = useState<{ kind: 'point' | 'device'; id: number; at: number } | undefined>();
  const openData = useCallback((kind: 'point' | 'device', id: number) => { setFocus({ kind, id, at: Date.now() }); setTab('data'); }, []);
  useEffect(() => { try { localStorage.removeItem('sappt_thlogger_tab'); } catch { /* 저장 불가 환경 */ } }, []);   // 예전 '마지막 탭' 기억값 정리
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('thlogger', 'WRITE');
  // 탭을 넘어가며 입력 창 열기(시작 단계 · 거절 로그 → 장치 등록 / 지점 등록) · 가이드 카테고리로 이동
  const [cmd, setCmd] = useState<Cmd>(null);
  const go = useCallback((k: 'newPoint' | 'newDevice', imei?: string) => { setCmd({ k, imei, at: Date.now() }); setTab(k === 'newPoint' ? 'points' : 'devices'); }, []);
  const [guideFocus, setGuideFocus] = useState<{ key: string; at: number } | undefined>();
  const openGuide = useCallback((key: string) => { setGuideFocus({ key, at: Date.now() }); setTab('guide'); }, []);
  const installedAny = points.some(p => p.is_active && p.current);
  const dummyN = devices.filter(d => d.is_active && isDummy(d)).length;
  const [dummyHide, setDummyHide] = useState(() => { try { return sessionStorage.getItem('sappt_thl_dummy_hide') === '1'; } catch { return false; } });

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([thloggerAPI.summary(), thloggerAPI.devices(true), thloggerAPI.points(true)])
      .then(([s, d, p]) => { setSummary(s.data); setDevices(d.data || []); setPoints(p.data || []); })
      .catch(() => message.error('온습도로거 정보를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 120000); return () => clearInterval(t); }, [reload]);

  return (
    <div>
      <SummaryBar summary={summary} devices={devices} picked={picked} onPick={setPicked} />
      {/* 더미(시연) 데이터가 남아 있는 채 실기기를 붙이면 화면 · 통계 · 알림에 섞인다 — 남아 있는 동안 알린다(이번 세션 동안 닫을 수 있다) */}
      {dummyN > 0 && !dummyHide && (
        <Alert type="warning" showIcon closable style={{ margin: '0 0 8px', padding: '4px 12px' }}
          onClose={() => { setDummyHide(true); try { sessionStorage.setItem('sappt_thl_dummy_hide', '1'); } catch { /* noop */ } }}
          message={<span style={{ fontSize: 12.5 }}>시연용 <b>더미 데이터 {dummyN}대</b>가 들어 있습니다(장치 대장의 <Tag color="purple" style={{ margin: 0, fontSize: 11, lineHeight: '16px', padding: '0 4px' }}>더미</Tag>).
            실제 기기 운영을 시작하기 전에 시스템 담당에게 <b>더미 초기화</b>를 요청하세요 — 실기기 데이터와 섞이지 않게.</span>} />
      )}
      {/* 탭 = 업무 순서: 보기(현황 · 측정 데이터) → 현장 관리(설치 지점 · 장치) → 진단(업링크 · 거절) → 참고(가이드).
          아이콘 + 글자 + 건수 배지로 구분하고 진단 탭은 문제가 있을 때만 경고 배지를 띄운다 */}
      <Tabs className="thl-tabs" activeKey={tab} onChange={setTab} size="small" items={[
        { key: 'overview', label: <span className="thl-tab"><AppstoreOutlined />현황</span>,
          // 설치된 지점이 하나도 없으면(초기 도입) 카드 대신 시작 단계를 보인다
          children: !loading && !installedAny
            ? <ThLoggerSetup points={points} devices={devices} rejects24h={summary?.rejects_24h || 0} canWrite={canWrite}
                onNewPoint={() => go('newPoint')} onNewDevice={() => go('newDevice')} onGoTab={setTab} onGuide={openGuide} />
            : <ThLoggerOverview points={points} devices={devices} onOpen={openData} reload={reload} /> },
        { key: 'data', label: <span className="thl-tab"><LineChartOutlined />측정 데이터</span>,
          children: <DataTab devices={devices} points={points} focus={focus} /> },
        { key: 'points', label: <span className="thl-tab"><EnvironmentOutlined />설치 지점<em>{points.filter(p => p.is_active).length}</em>
            {points.some(p => p.is_active && !p.current) && <i className="warn">기기 없음 {points.filter(p => p.is_active && !p.current).length}</i>}</span>,
          children: <ThLoggerPoints points={points} devices={devices} loading={loading} reload={reload} onOpenData={id => openData('point', id)} cmd={cmd} /> },
        { key: 'devices', label: <span className="thl-tab"><HddOutlined />장치 대장<em>{devices.filter(d => d.is_active).length}</em></span>,
          children: <DevicesTab devices={devices} loading={loading} reload={reload} picked={picked} cmd={cmd} /> },
        { key: 'uplinks', label: <span className="thl-tab"><CloudUploadOutlined />업링크 로그</span>, children: <UplinksTab devices={devices} /> },
        { key: 'rejects', label: <span className="thl-tab"><ExclamationCircleOutlined />거절 로그{summary?.rejects_24h ? <i className="warn">24h {summary.rejects_24h}</i> : null}</span>,
          children: <RejectsTab devices={devices} onRegister={imei => go('newDevice', imei)} onGoTab={setTab} /> },
        { key: 'guide', label: <span className="thl-tab"><BookOutlined />설정 가이드</span>, children: <ThLoggerGuide onGoTab={setTab} focus={guideFocus} /> },
      ]} />
    </div>
  );
};

export default ThLoggerPage;
